import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql } from '@common/hooks';
import { DetailItemAttachments } from '@onboarding/graphql/fragments';

const AttachDetailOnWorksListItem = gql`
  mutation AttachDetailOnWorksListItem(
    $id: ID!
    $input: AttachDetailOnWorksListItemInput!
  ) {
    attachDetailOnWorksListItem(id: $id, input: $input) {
      worksListItem {
        id
        state
        compliant
        compliantForAssignment
        revisedExpired
        revisedExpiresOn
        revisedCompliantForAssignment
        isRevisedExpiringSoon
        detailItem {
          ... on License {
            id
            stateCode
            expiresOn
            number
            compact
            licenseType
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseAssessment {
            id
            expiresOn
            completedOn
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseHealthDoc {
            id
            completedOn
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseCertification {
            id
            name
            expiresOn
            issuingBody
            certificationUuid
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on Document {
            id
            expiresOn
            completedOn
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
        }
      }
    }
  }
  ${DetailItemAttachments}
`;

const WorksListItemFragment = gql`
  fragment WorksListItemFragment on WorksListItem {
    id
    active
    name
    type
    typeName
    description

    ... on BackgroundIdentificationItem {
      documentTemplateSubtype
      documentTemplate {
        id
      }
    }
    ... on ImmigrationItem {
      documentTemplateSubtype
      documentTemplate {
        id
      }
    }
    ... on CertificationItem {
      certification {
        id
      }
    }
    ... on LicenseItem {
      licenseType
    }
    ... on AssessmentItem {
      issuedAssessment {
        id
      }
    }
    ... on HealthDocumentItem {
      healthDocSubtype
      healthDoc {
        id
      }
    }
  }
`;

const CreateBackgroundIdentificationItem = gql`
  mutation CreateBackgroundIdentificationItem(
    $worksListId: ID!
    $input: CreateBackgroundIdentificationItemInput!
  ) {
    createBackgroundIdentificationItem(
      worksListId: $worksListId
      input: $input
    ) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateBackgroundIdentificationItem = gql`
  mutation UpdateBackgroundIdentificationItem(
    $itemId: ID!
    $input: UpdateBackgroundIdentificationItemInput!
  ) {
    updateBackgroundIdentificationItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateImmigrationItem = gql`
  mutation CreateImmigrationItem(
    $worksListId: ID!
    $input: CreateImmigrationItemInput!
  ) {
    createImmigrationItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateImmigrationItem = gql`
  mutation UpdateImmigrationItem(
    $itemId: ID!
    $input: UpdateImmigrationItemInput!
  ) {
    updateImmigrationItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateLicenseItem = gql`
  mutation CreateLicenseItem(
    $worksListId: ID!
    $input: CreateLicenseItemInput!
  ) {
    createLicenseItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateLicenseItem = gql`
  mutation UpdateLicenseItem($itemId: ID!, $input: UpdateLicenseItemInput!) {
    updateLicenseItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateCertificationItem = gql`
  mutation CreateCertificationItem(
    $worksListId: ID!
    $input: CreateCertificationItemInput!
  ) {
    createCertificationItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateCertificationItem = gql`
  mutation UpdateCertificationItem(
    $itemId: ID!
    $input: UpdateCertificationItemInput!
  ) {
    updateCertificationItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateAssessmentItem = gql`
  mutation CreateAssessmentItem(
    $worksListId: ID!
    $input: CreateAssessmentItemInput!
  ) {
    createAssessmentItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateAssessmentItem = gql`
  mutation UpdateAssessmentItem(
    $itemId: ID!
    $input: UpdateAssessmentItemInput!
  ) {
    updateAssessmentItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateHealthDocumentItem = gql`
  mutation CreateHealthDocumentItem(
    $worksListId: ID!
    $input: HealthDocumentItemInput!
  ) {
    createHealthDocumentItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateHealthDocumentItem = gql`
  mutation UpdateHealthDocumentItem(
    $itemId: ID!
    $input: UpdateHealthDocumentItemInput!
  ) {
    updateHealthDocumentItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const CreateOtherDocumentItem = gql`
  mutation CreateOtherDocumentItem(
    $worksListId: ID!
    $input: CreateOtherDocumentItemInput!
  ) {
    createOtherDocumentItem(worksListId: $worksListId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

const UpdateOtherDocumentItem = gql`
  mutation UpdateOtherDocumentItem(
    $itemId: ID!
    $input: UpdateOtherDocumentItemInput!
  ) {
    updateOtherDocumentItem(itemId: $itemId, input: $input) {
      worksListItem {
        id
        ...WorksListItemFragment
      }

      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemFragment}
  ${FullValidationErrorFragment}
`;

export {
  AttachDetailOnWorksListItem,
  CreateAssessmentItem,
  CreateBackgroundIdentificationItem,
  CreateCertificationItem,
  CreateHealthDocumentItem,
  CreateImmigrationItem,
  CreateLicenseItem,
  CreateOtherDocumentItem,
  UpdateAssessmentItem,
  UpdateBackgroundIdentificationItem,
  UpdateCertificationItem,
  UpdateHealthDocumentItem,
  UpdateImmigrationItem,
  UpdateLicenseItem,
  UpdateOtherDocumentItem,
};
